import { useState, useEffect, useCallback, useRef } from 'react';
import './CompanyScoreWidget.css';
import CompanyScoreChart from '../../Components/CompanyScoreChart/CompanyScoreChart';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';
import {
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';
import { ReactComponent as CompanyScoreInfoIcon } from '../../assets/icons/tooltip-company-score-icon.svg';
import InfoPanel from '../../Components/Common/InfoPanel/InfoPanel';
import { ReactComponent as CrownIcon } from '../../assets/icons/vip-crown-2-line.svg';

import { getComapnyScore } from '../../api/company.api';
import { CompanyScoreDataType } from '../../types/comapnyscore.type';
import useResizeNotifier from '../../hooks/useResizeNotifier';

export interface CompanyScoreWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const COMPANY_SCORE_INFO_TITLE = 'About Stock Reports Plus ratings';

const COMPANY_SCORE_INFO_CONTENT = (
  <>
    <p>
      Stock Reports Plus ratings are systematically generated using LSEG data to
      support investment research and decision-making. Stocks are evaluated
      relative to others in the market, helping investors identify stronger
      investment opportunities.
    </p>
    <p className="info-panel-heading">Component scores</p>
    <p>
      Each component provides an independent, quantitative assessment and is
      scored from 1 to 10, with 10 being the most favourable.
    </p>
    <p className="info-panel-heading">Earnings score</p>
    <p>
      Evaluates earnings surprise history and analyst sentiment, including
      recommendation changes and earnings estimate revisions. Positive surprises
      and recent analyst upgrades tend to result in higher scores.
    </p>
    <p className="info-panel-heading">Fundamental score</p>
    <p>
      Evaluates financial strength using factors such as margins, debt, earnings
      quality and dividend growth. Companies that do not pay dividends are not
      penalised.
    </p>
    <p className="info-panel-heading">Relative valuation score</p>
    <p>
      Evaluates valuation measures including Price to Sales, Trailing P/E,
      Forward P/E and Forward PEG. Lower valuations relative to the market and
      historical norms tend to result in higher scores.
    </p>
    <p className="info-panel-heading">Risk score</p>
    <p>
      Evaluates short- and long-term risk using measures such as volatility,
      return patterns, beta and correlation. Larger companies with more
      consistent price returns tend to receive higher scores.
    </p>
    <p className="info-panel-heading">Price momentum score</p>
    <p>
      Evaluates relative price strength and historical seasonality. Strong
      recent performance combined with favourable seasonal trends tends to
      result in higher scores.
    </p>
  </>
);

const CompanyScoreWidget = (props: CompanyScoreWidgetProps) => {
  const { onHeightChange } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  useResizeNotifier(
    containerRef,
    WidgetNameSource.CompanyScore,
    onHeightChange,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  useEffect(() => {
    if (!isInfoOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsInfoOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isInfoOpen]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'companyScore');
  const [error, setError] = useState<string | null>(null);
  // const [data, setData] = useState<CompanyScoreDataType[]>([]);

  const [earnings, setEarnings] = useState<number[] | undefined>(undefined);
  const [pricemomentum, setPricemomentum] = useState<number[] | undefined>(
    undefined,
  );
  const [risk, setRisk] = useState<number[] | undefined>(undefined);
  const [fundamental, setFundamental] = useState<number[] | undefined>(
    undefined,
  );
  const [relativevaluation, setRelativevaluation] = useState<
    number[] | undefined
  >(undefined);

  const [symbol, setSymbol] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [score, setScore] = useState<string | null>('-');
  const [scoreClass, setScoreClass] = useState<string>('score');
  const [outlook, setOutlook] = useState<string | null>(null);
  const [ranking, setRanking] = useState<number | null>(null);
  const [rankingTotal, setRankingTotal] = useState<number | null>(null);

  // Call API when page changes
  useEffect(() => {
    fetchRecommendationData();
  }, [props.token, props.symbol]);

  const fetchRecommendationData = useCallback(async () => {
    setIsLoading(true);

    setError(null);
    setIsEmptyData(false);

    try {
      const res = await getComapnyScore(props.symbol || '', props.token || '');

      if (res.data) {
        if (res.data[0]) {
          setSymbol(res.data[0].code);
          setOutlook(res.data[0].outlook);
          setScore(res.data[0].avg_score);
          setRanking(res.data[0].ranking);
          setRankingTotal(res.data[0].ranking_total);

          if (res.data[0].avg_score === 10) setScoreClass('score10');
          else setScoreClass('score');
        }

        if (res.data[1]) {
          setName(res.data[1].name);
        }

        setEarnings([
          res.data[0].earn_score ? res.data[0].earn_score : -1,
          res.data[1].earn_score ? res.data[1].earn_score.toFixed(2) : -1,
        ]);
        setPricemomentum([
          res.data[0].price_score ? res.data[0].price_score : -1,
          res.data[1].price_score ? res.data[1].price_score.toFixed(2) : -1,
        ]);
        setRisk([
          res.data[0].risk_score ? res.data[0].risk_score : -1,
          res.data[1].risk_score ? res.data[1].risk_score.toFixed(2) : -1,
        ]);
        setFundamental([
          res.data[0].fund_score ? res.data[0].fund_score : -1,
          res.data[1].fund_score ? res.data[1].fund_score.toFixed(2) : -1,
        ]);
        setRelativevaluation([
          res.data[0].val_score ? res.data[0].val_score : -1,
          res.data[1].val_score ? res.data[1].val_score.toFixed(2) : -1,
        ]);
      } else {
        setIsEmptyData(true);
        props.onNoData?.({
          type: WidgetMessageType.NoData,
          source: 'CompanyScoreWidget',
          data: null,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch recommendation data');
      console.error('Error fetching recommendation data:', err);
      props.onError?.({
        message: err.message,
        type: err.name + '_' + err.code,
      });
      setIsEmptyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [props.token, props.symbol]);

  return (
    <Layout theme={props?.theme || ThemeType.Direct}>
      <div className="company-score-wrapper">
        <div className="company-score-container" ref={containerRef}>
          {isLoading && !props.onLoadingChange ? (
            <Loading />
          ) : !isLoading && isEmptyData ? (
            <ErrorMessage msg={msgItem} />
          ) : (
            <>
              <div className="company-score-analysts-rating-container">
                <div className="company-score-analysts-rating-row">
                  <div
                    className={`company-score-analysts-rating ${scoreClass}`}
                  >
                    {score ? score : '-'}
                  </div>
                  <button
                    type="button"
                    className="company-score-info-button"
                    aria-label={COMPANY_SCORE_INFO_TITLE}
                    onClick={() => setIsInfoOpen(true)}
                  >
                    <CompanyScoreInfoIcon />
                  </button>
                </div>
                  {ranking != null && rankingTotal != null && (
                    <div className="company-score-analysts-rating-ranking">
                      <CrownIcon className="company-score-analysts-rating-ranking-icon" />
                      <span className="company-score-analysts-rating-ranking-text">
                        Ranks {ranking} of {rankingTotal} in same sector
                      </span>
                    </div>
                  )}
                <div className="company-score-analysts-rating-text">
                  {outlook ? outlook : '-'}
                </div>
              </div>

              <div className="company-score-chart-container">
                <CompanyScoreChart
                  symbol={symbol ? symbol : '-'}
                  name={name ? name : '-'}
                  earnings={earnings ? earnings : [-1, -1]}
                  pricemomentum={pricemomentum ? pricemomentum : [-1, -1]}
                  risk={risk ? risk : [-1, -1]}
                  fundamental={fundamental ? fundamental : [-1, -1]}
                  relativevaluation={
                    relativevaluation ? relativevaluation : [-1, -1]
                  }
                />
              </div>

              {isInfoOpen && (
                <InfoPanel
                  title={COMPANY_SCORE_INFO_TITLE}
                  onClose={() => setIsInfoOpen(false)}
                >
                  {COMPANY_SCORE_INFO_CONTENT}
                </InfoPanel>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CompanyScoreWidget;
